// AyaPriveAuthorizations — Profil → AYA Privé : autorisations révocables + limites
// + journal des actions (receipts du PRIVATE ACTION ENGINE).
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR").replace(/\u202F|\u00A0/g, " ")} FCFA`;

const TOGGLES = [
  { k: "consult_addresses", label: "Consulter mes adresses", icon: "📍" },
  { k: "search_for_me", label: "Rechercher pour moi", icon: "🔎" },
  { k: "cart_add", label: "Ajouter au panier", icon: "🛒" },
  { k: "prepare_bookings", label: "Préparer des réservations", icon: "📋" },
  { k: "taxi_order", label: "Commander un taxi", icon: "🚕" },
  { k: "auto_pay", label: "Payer automatiquement", icon: "💳", sensitive: true },
  { k: "buy_tickets", label: "Acheter des billets", icon: "🎫", sensitive: true },
  { k: "edit_sensitive", label: "Modifier mes informations sensibles", icon: "🛡️", sensitive: true },
];

export default function AyaPriveAuthorizations({ userId }) {
  const [auth, setAuth] = useState(null);
  const [receipts, setReceipts] = useState(null);

  const key = () => {
    const token = localStorage.getItem("civ_user_token") || "";
    return { headers: token ? { Authorization: `Bearer ${token}` } : {}, params: { user_id: userId } };
  };

  const load = async () => {
    try {
      const [a, r] = await Promise.all([
        axios.get(`${API}/v3/private/authorizations`, key()),
        axios.get(`${API}/v3/private/receipts`, key()),
      ]);
      if (a.data?.ok) setAuth(a.data.authorizations);
      if (r.data?.ok) setReceipts(r.data.receipts);
    } catch (_) { toast.error("Impossible de charger les autorisations"); }
  };
  useEffect(() => { if (userId) load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (patch) => {
    const next = { ...auth, ...patch };
    setAuth(next);
    try {
      const token = localStorage.getItem("civ_user_token") || "";
      await axios.post(`${API}/v3/private/authorizations`, { user_id: userId, settings: next },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      toast.success("Autorisations AYA Privé mises à jour ✓");
    } catch (_) { toast.error("Échec de la mise à jour"); }
  };

  if (!auth) return <div style={{ padding: 20, textAlign: "center", color: "#9DB4A6", fontSize: 13 }} data-testid="aya-prive-loading">⏳ Chargement…</div>;

  return (
    <div data-testid="aya-prive-tab" style={{ display: "grid", gap: 14 }}>
      <div style={card}>
        <div style={eyebrow}>🤖 AYA Privé · Autorisations</div>
        <div style={{ fontSize: 12, color: "rgba(236,245,238,0.6)", marginBottom: 10 }}>
          Ce que ton agent personnel AYA a le droit de faire pour toi. Révocable à tout moment.
        </div>
        {TOGGLES.map((t) => (
          <label key={t.k} data-testid={`aya-prive-toggle-${t.k}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.07)", cursor: "pointer" }}>
            <span style={{ fontSize: 16 }}>{t.icon}</span>
            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: t.sensitive ? "#F2C66B" : "#ECF5EE" }}>
              {t.label}{t.sensitive && <span style={{ fontSize: 10, marginLeft: 6, color: "#FCA5A5" }}>SENSIBLE</span>}
            </span>
            <input type="checkbox" checked={!!auth[t.k]} onChange={(e) => update({ [t.k]: e.target.checked })} style={{ width: 18, height: 18, accentColor: "#EFB94C" }} />
          </label>
        ))}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: "#D9B45B", marginBottom: 6 }}>Limites automatiques</div>
          {[["taxi_auto_limit_fcfa", "Taxi — automatique jusqu'à"], ["commerce_auto_limit_fcfa", "Commerce — automatique jusqu'à"]].map(([k, label]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
              <span style={{ flex: 1, fontSize: 12.5, color: "rgba(236,245,238,0.75)" }}>{label}</span>
              <input
                type="number" min="0" step="500" value={auth[k] || 0}
                data-testid={`aya-prive-limit-${k}`}
                onChange={(e) => setAuth({ ...auth, [k]: Number(e.target.value) || 0 })}
                onBlur={(e) => update({ [k]: Number(e.target.value) || 0 })}
                style={{ width: 110, padding: "7px 9px", borderRadius: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", color: "#fff", fontSize: 13, fontWeight: 700 }}
              />
              <span style={{ fontSize: 11, color: "#9DB4A6" }}>FCFA</span>
            </div>
          ))}
          <div style={{ fontSize: 10.5, color: "rgba(236,245,238,0.45)", marginTop: 4 }}>0 = toujours me demander confirmation.</div>
        </div>
      </div>

      <div style={card}>
        <div style={eyebrow}>🧾 Journal des actions</div>
        {receipts === null && <div style={{ fontSize: 12, color: "#9DB4A6" }}>⏳ Chargement…</div>}
        {receipts && receipts.length === 0 && <div style={{ fontSize: 12.5, color: "#9DB4A6" }} data-testid="aya-prive-receipts-empty">Aucune action AYA Privé pour l'instant. Essaie : « AYA commande-moi un taxi de la maison à la cathédrale ».</div>}
        {(receipts || []).map((r) => (
          <div key={r.receipt_id} data-testid={`aya-prive-receipt-${r.receipt_id}`} style={{ padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 800 }}>{r.action_id}</span>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: r.status === "executed" ? "#2EE27B" : r.status === "prepared" ? "#F2C66B" : "#FCA5A5" }}>{r.status?.toUpperCase()}</span>
            </div>
            <div style={{ fontSize: 11.5, color: "rgba(236,245,238,0.65)", marginTop: 2 }}>« {r.intent} »</div>
            <div style={{ fontSize: 10.5, color: "rgba(236,245,238,0.45)", marginTop: 2 }}>
              {[r.result, r.amount_fcfa ? fmt(r.amount_fcfa) : null, (r.timestamp || "").slice(0, 16).replace("T", " · "), r.audit_id].filter(Boolean).join(" · ")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const card = { borderRadius: 16, padding: "14px 15px", background: "linear-gradient(160deg, #0A3A22 0%, #062818 60%, #051F13 100%)", border: "1px solid rgba(201,162,75,0.4)", color: "#ECF5EE", fontFamily: "'Manrope','Inter',sans-serif" };
const eyebrow = { fontSize: 10.5, fontWeight: 800, letterSpacing: 2.5, textTransform: "uppercase", color: "#D9B45B", marginBottom: 8 };
